import type { CSSProperties } from 'react';
import { useApiQuery } from '../use-api.js';
import {
  ALARMS_PATH,
  ALARMS_REFETCH_MS,
  formatChangedAt,
  statePresentation,
  type AlarmRecord,
  type AlarmsResponse,
} from '../lib/alarms.js';

/**
 * gh#351: the only place this stack's CloudWatch alarms report to.
 *
 * The endpoint returns them already ordered ALARM → NO DATA → OK, so the page
 * does not re-sort: what arrives first is what matters most.
 */
export function Alarms() {
  const { data, isLoading, error } = useApiQuery<AlarmsResponse>(
    ['alarms'],
    ALARMS_PATH,
    { refetchInterval: ALARMS_REFETCH_MS },
  );

  return (
    <div>
      <h2 style={headingStyle}>Alarms</h2>
      <p style={subtitleStyle}>
        CloudWatch alarms on the Death Star API stack. These alarms have no notification
        actions — this page is where they report, so a firing alarm is seen when someone
        opens the portal rather than pushed to a channel.
      </p>

      {isLoading && <p style={mutedStyle}>Loading alarm state…</p>}
      {error && (
        <p className="error">
          Failed to read alarm state: {error instanceof Error ? error.message : String(error)}.
          Alarm state is unknown — do not read this as all-clear.
        </p>
      )}

      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
            <Tile label="Firing" value={data.in_alarm_count} tone="ALARM" />
            <Tile label="No data" value={data.insufficient_data_count} tone="INSUFFICIENT_DATA" />
            <Tile label="Total watched" value={data.alarms.length} tone="OK" />
          </div>

          {/* An incomplete listing must say so, and must say which way it is
              incomplete. Silently showing a short list is how a missing alarm
              gets mistaken for a healthy one. */}
          {data.truncated && (
            <p className="error">
              Alarm listing was truncated — some alarms are not shown below.
            </p>
          )}
          {data.dropped > 0 && (
            <p className="error">
              {data.dropped} alarm{data.dropped === 1 ? '' : 's'} could not be read (no name
              returned) and {data.dropped === 1 ? 'is' : 'are'} not shown below.
            </p>
          )}

          {data.alarms.length === 0 && (
            <p style={mutedStyle}>
              No alarms found on this stack. That is not an all-clear — it means the
              listing came back empty, which is worth investigating.
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data.alarms.map((a) => <AlarmCard key={a.name} alarm={a} />)}
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: number; tone: AlarmRecord['state'] }) {
  const p = statePresentation(tone);
  // Zero is the good news for the two not-OK tiles, so it stays neutral rather
  // than painting a green "0 firing" tile red.
  const emphasise = value > 0 && tone !== 'OK';
  return (
    // marginBottom: 0 because the .card class carries its own 20px bottom margin,
    // which would stack on top of the row's gap and its container's margin.
    <div className="card" style={{ padding: 'var(--loop-space-200)', marginBottom: 0, flex: 1, minWidth: 140, background: emphasise ? p.background : undefined }}>
      <div style={{ color: 'var(--loop-neutral-600)', fontSize: '0.75rem', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 664, color: emphasise ? p.color : 'var(--loop-neutral-1100)' }}>{value}</div>
    </div>
  );
}

function AlarmCard({ alarm }: { alarm: AlarmRecord }) {
  const p = statePresentation(alarm.state);
  return (
    <div className="card" style={{ padding: 'var(--loop-space-200)', marginBottom: 0, borderLeft: `3px solid ${p.color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ ...badgeStyle, background: p.background, color: p.color }}>{p.label}</span>
        <span style={{ fontFamily: 'monospace', fontSize: '0.875rem', color: 'var(--loop-neutral-1100)', fontWeight: 528 }}>
          {alarm.name}
        </span>
        <span style={{ color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>
          changed {formatChangedAt(alarm.state_updated_at)}
        </span>
        <a
          href={alarm.console_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: 'auto', color: 'var(--loop-blue-600)', fontSize: '0.75rem' }}
        >
          CloudWatch ↗
        </a>
      </div>

      {/* alarmDescription, verbatim. These carry the actual remediation — the
          slicing alarm names the knob to turn, the blocking-share one names the
          confidence-85 fingerprint to check and warns off the weekly drift
          detector — so summarising would throw away the useful half. */}
      {alarm.description && (
        <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--loop-neutral-900)', lineHeight: 1.5 }}>
          {alarm.description}
        </p>
      )}

      {alarm.state_reason && (
        <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: 'var(--loop-neutral-600)', fontFamily: 'monospace' }}>
          {alarm.state_reason}
        </p>
      )}
    </div>
  );
}

const headingStyle: CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 664,
  marginBottom: 8,
  color: 'var(--loop-neutral-1100)',
};

const subtitleStyle: CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--loop-neutral-700)',
  marginTop: 0,
  marginBottom: 24,
  maxWidth: 720,
  lineHeight: 1.5,
};

const mutedStyle: CSSProperties = { color: 'var(--loop-neutral-700)', fontSize: '0.875rem' };

const badgeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '999px',
  fontSize: '0.6875rem',
  fontWeight: 664,
  letterSpacing: '0.03em',
};
